import { logger as skypointLogger } from "./logger-init";
import { useEffect, useState, lazy, Suspense } from "react";
skypointLogger.info("SkySupport client app loaded");
import { BrowserRouter, Routes, Route } from "react-router";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { SkyProvider, SkyButton, createSkyTheme, useSkyTheme } from "@skypointcloud/skypoint-ui";
import "@skypointcloud/skypoint-ui/styles.css";
// React Flow base stylesheet — loaded as a raw string and injected
// at runtime to bypass Vite's CSS pipeline entirely. The
// @tailwindcss/vite plugin strips ALL third-party CSS rules (only
// bare @keyframes survive) from any CSS that flows through its
// processing, including from JS-imported CSS modules and CSS
// @imports. This broke the workflow editor on QA: status nodes
// rendered at the default ~30px size with no edges visible. The
// `?inline` import returns the file contents as a string without
// processing it, then we manually attach it via a <style> tag.
// Local dev was unaffected because Vite serves CSS modules
// unbundled (no Tailwind processing) in dev mode.
import xyflowStyles from "@xyflow/react/dist/style.css?raw";
const xyflowStyleEl = document.createElement("style");
xyflowStyleEl.setAttribute("data-source", "xyflow-react");
xyflowStyleEl.textContent = xyflowStyles;
document.head.appendChild(xyflowStyleEl);
import { Headset } from "lucide-react";
import {
  MsalProvider,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";
import { createMsalInstance } from "./config/msalConfig";
import { authService as auth, setMsalInstance } from "./utils/authService";
import SkypointLogoLoader from "./components/SkypointLogoLander/SkypointLogoLoader";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { useB2CPolicies } from "./hooks/useB2CPolicies";
import { ROUTES } from "./components/shared/constant";
import Callback from "./components/callback";
import { store, persistor } from "./store";
import "./index.css";
import AppNew from "./AppNew";
import { InstanceIdUrlEnforcer } from "./components/InstanceIdUrlEnforcer";
import { SpinnerFix } from "./components/shared/SpinnerFix";
import AccountLocked from "./components/icons/accountLocked";
import { MessageBlock } from "./components/MessageBlock";
import { ConfirmationProvider } from "./components/shared/confirm";
import {
  getAndClearOAuthReturnPath,
  captureOAuthCallbackStatus,
  storeOAuthReturnPath,
  rememberReturnPath,
} from "./utils/oauthReturnHandler";
import { AnalyticsProvider } from "./components/AnalyticsProvider";
import { signalAppReady, onAppReady } from "./utils/appLoadingSignal";
import { useDispatch } from "react-redux";
import { SignInPage } from "./views/public/SignIn";
import {
  activePhoneSession,
  clearPhoneSession,
  PHONE_SESSION_KEY,
  settleTabSignIn,
  type PhoneSession,
} from "./utils/phoneSession";
import { reloadIfNewerVersion } from "./utils/newVersion";
import { setTokens } from "./store/slices/authSlice";

captureOAuthCallbackStatus();
// Migrate: clear any stale return-path entry that the old localStorage-based
// implementation may have left behind (now stored in sessionStorage).
localStorage.removeItem("skyadvisor_oauth_pending_return");

// When a user lands on a deep link (e.g. from an email "View Ticket" button) in a
// new tab with no prior in-app navigation, the browser back button has nowhere to go
// and shows a Bad Request (expired B2C OAuth page) or exits the tab. Inject the
// dashboard root into the history stack first so back always returns to the dashboard.
(function injectDashboardForDeepLinks() {
  const path = window.location.pathname;
  if (
    path !== "/" &&
    !path.startsWith("/callback") &&
    !path.startsWith("/survey") &&
    window.history.length <= 1
  ) {
    const fullUrl = window.location.href;
    window.history.replaceState(null, "", "/");
    window.history.pushState(null, "", fullUrl);
  }
})();

const SurveyPage = lazy(() =>
  import("./views/public/SurveyPage").then((m) => ({ default: m.SurveyPage })),
);

const skyTheme = createSkyTheme({ brandColor: "#F78A05" });

/**
 * Syncs SkyProvider's mode to <html> so Tailwind dark: utilities work.
 * SkyProvider uses sky-dark/sky-light; Tailwind needs .dark on documentElement.
 */
const ThemeSyncer = () => {
  const { isDarkMode } = useSkyTheme();
  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDarkMode);
  }, [isDarkMode]);
  return null;
};

declare global {
  interface Window {
    instanceCultureName: string;
    instanceTimezone: string;
    allowEdit: any;
    allowDelete: any;
    allowView: any;
    isFullAccess: boolean;
    isPlatformAdmin: boolean;
    isTenantAdmin: boolean;
    isInstanceAdmin: boolean;
  }
}

/**
 * ApplicationBlock — renders the authenticated app.
 *
 * B2C token proves identity. The callback flow (createAuthSession) handles
 * user validation and account activation. No separate checkUserExists call needed.
 */
const ApplicationBlock = () => {
  const { instance } = useMsal();

  // Check if account is enabled using idTokenClaims
  const currentUser = auth?.currentUser();
  const isAccountEnabled = currentUser?.isAccountEnabled !== false;

  useEffect(() => {
    if (!isAccountEnabled) {
      signalAppReady();
    }
  }, [isAccountEnabled]);

  if (!isAccountEnabled) {
    return (
      <MessageBlock>
        <AccountLocked />
        <h3>
          Your account has been locked. Contact your support person to unlock it, then try again.
        </h3>
        <h4>
          Click here to{" "}
          <a
            href="#"
            onClick={() => instance.logoutRedirect()}
          >
            Sign out
          </a>
        </h4>
        <a href="https://skypointcloud.com/customer-support/">
          <SkyButton
            appearance="primary"
            icon={Headset}
            className="cursor-pointer text-white bg-[rgb(247,138,5)] border border-[rgb(247,138,5)] text-center px-4"
          >
            Need help?
          </SkyButton>
        </a>
      </MessageBlock>
    );
  }

  return (
    <BrowserRouter>
      <InstanceIdUrlEnforcer />
      <AnalyticsProvider>
        <Routes>
          <Route
            path={ROUTES.CALLBACK}
            element={<Callback />}
          />
          <Route
            path="*"
            element={
              <ProtectedRoute>
                <AppNew />
              </ProtectedRoute>
            }
          />
        </Routes>
      </AnalyticsProvider>
    </BrowserRouter>
  );
};

/** A failed sign-in start, in words for the person. */
function loginErrorMessage(error: unknown): string {
  const authError = error as { errorCode?: string; errorMessage?: string; message?: string };
  if (authError?.errorCode === "popup_window_error") {
    return "Popup was blocked. Please allow popups for this site and try again.";
  }
  if (authError?.errorCode === "user_cancelled") {
    return "Login was cancelled. Click below to try again.";
  }
  if (authError?.errorCode === "popup_blocked_reload_required") {
    return "Please enable popups or reload the page to sign in.";
  }
  if (authError?.errorCode === "interaction_in_progress") {
    return "Authentication is already in progress. Please reload the page.";
  }
  if (authError?.errorCode === "redirect_loop_detected") {
    return "Sign-in keeps being sent back to the start. Click below to try again; if it keeps happening, clear this site's data or contact support.";
  }
  return authError?.errorMessage || authError?.message || "Login failed. Please try again.";
}

/** Whether someone is typing in a field: reloading then would lose it. */
function isTyping(): boolean {
  const el = document.activeElement;
  return (
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    (el instanceof HTMLElement && el.isContentEditable)
  );
}

/** The sign-in page (on unless VITE_SIGN_IN_PAGE is "false"). */
const SIGN_IN_PAGE = import.meta.env.VITE_SIGN_IN_PAGE !== "false";

/**
 * Shown when nobody is signed in: the sign-in page, with a button per provider
 * and, when it's turned on, signing in with a mobile number.
 */
const SignInGate = () => {
  const [providerError, setProviderError] = useState<string | null>(null);
  const [providerBusy, setProviderBusy] = useState(false);

  useEffect(() => {
    // A tab still running an older version shows the older sign-in page: when
    // another one is deployed, reload into it, but only before anyone does
    // anything (a sign-in already started mustn't be cut off).
    let touched = false;
    const touch = () => {
      touched = true;
    };
    window.addEventListener("pointerdown", touch, { capture: true, once: true });
    window.addEventListener("keydown", touch, { capture: true, once: true });
    void reloadIfNewerVersion(() => !touched && !isTyping());
    rememberReturnPath();
    // The sign-in page lives at the site itself, whatever page was asked for
    // (as in skySchedule); that page is kept above and opened after sign-in.
    const { pathname, search } = window.location;
    if ((pathname !== "/" || search) && !pathname.startsWith("/callback")) {
      window.history.replaceState(window.history.state, "", "/");
    }
    return () => {
      // Signed in (or gone) meanwhile: a check still under way mustn't reload.
      touched = true;
      window.removeEventListener("pointerdown", touch, { capture: true });
      window.removeEventListener("keydown", touch, { capture: true });
    };
  }, []);

  const startProviderSignIn = (domainHint?: string) => {
    if (providerBusy) return;
    setProviderBusy(true);
    setProviderError(null);
    // One way in at a time: a Microsoft sign-in replaces any phone one.
    clearPhoneSession();
    rememberReturnPath();
    auth
      .login(domainHint)
      .then(() => {
        // Popup sign-in resolves here; MsalProvider then shows the app.
        setProviderBusy(false);
      })
      .catch((error: unknown) => {
        skypointLogger.error("Login initiation failed", error instanceof Error ? error : undefined);
        setProviderBusy(false);
        setProviderError(loginErrorMessage(error));
      });
  };

  return (
    <SignInPage
      onProviderSignIn={startProviderSignIn}
      providerError={providerError}
      providerBusy={providerBusy}
    />
  );
};

/** Shown when nobody is signed in. */
const LoginHandler = () => (SIGN_IN_PAGE ? <SignInGate /> : <AutoLoginHandler />);

/**
 * Login Handler Component (when the sign-in page is turned off)
 * Automatically triggers login when user is not authenticated
 * Uses popup for iframes, redirect for top-level windows
 */
const AutoLoginHandler = () => {
  const { instance, inProgress } = useMsal();
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  useEffect(() => {
    // Trigger login if no interaction is in progress and we're not already logging in
    if (inProgress === "none" && !loginError && !isLoggingIn) {
      setIsLoggingIn(true);

      // Store the current URL so we can redirect back after login completes
      const currentPath = window.location.pathname + window.location.search;
      if (currentPath !== "/" && !currentPath.startsWith("/callback")) {
        storeOAuthReturnPath(currentPath);
      }

      auth
        .login()
        .then(() => {
          // For popup login, this resolves after successful login
          // The MsalProvider will automatically update the auth state
          skypointLogger.info("Login completed successfully");
        })
        .catch((error) => {
          skypointLogger.error(
            "Login initiation failed",
            error instanceof Error ? error : undefined,
          );
          setIsLoggingIn(false);

          setLoginError(loginErrorMessage(error));
        });
    }
  }, [instance, inProgress, loginError, isLoggingIn]);

  useEffect(() => {
    if (loginError) signalAppReady();
  }, [loginError]);

  if (loginError) {
    return (
      <MessageBlock>
        <h3>{loginError}</h3>
        <h4>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setLoginError(null);
              setIsLoggingIn(false);
            }}
          >
            Click here to try again
          </a>
        </h4>
      </MessageBlock>
    );
  }

  return null;
};

/**
 * The app for someone signed in with a mobile number. Puts who they are where a
 * Microsoft sign-in puts its token claims, for the views that read them.
 */
const PhoneSignedInApp = ({ session }: { session: PhoneSession }) => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(
      setTokens({
        accessToken: session.token,
        idTokenClaims: { email: session.email, emails: [session.email], name: session.name },
      }),
    );
  }, [dispatch, session.token, session.email, session.name]);
  // Signed out (or in as someone else) in another tab: this tab follows, back
  // to the sign-in page, rather than carrying on with a pass that's gone.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== null && e.key !== PHONE_SESSION_KEY) return;
      if (activePhoneSession()?.token !== session.token) window.location.replace("/");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [session.token]);
  return <ApplicationBlock />;
};

/**
 * Authenticated App Wrapper
 * Uses MSAL React templates for authentication state management
 */
const AuthenticatedApp = () => {
  const { inProgress, accounts } = useMsal();
  const [oauthReturnHandled, setOauthReturnHandled] = useState(false);

  // Phase 7: Handle B2C policies (password reset, error codes, etc.)
  useB2CPolicies();

  useEffect(() => {
    if (inProgress === "none" && !oauthReturnHandled) {
      // Skip redirect when on /callback — callback.tsx handles the return path
      // after the session is fully established (avoids navigating before session API completes)
      if (window.location.pathname === "/callback") {
        setOauthReturnHandled(true);
        return;
      }
      const result = getAndClearOAuthReturnPath();
      if (result && window.location.pathname !== result.path.split("?")[0]) {
        let targetUrl = result.path;

        if (result.oauthStatus) {
          const separator = result.path.includes("?") ? "&" : "?";
          targetUrl = `${result.path}${separator}oauth=${result.oauthStatus.status}`;
          if (result.oauthStatus.message) {
            targetUrl += `&message=${encodeURIComponent(result.oauthStatus.message)}`;
          }
        }

        window.location.replace(targetUrl);
      } else {
        setOauthReturnHandled(true);
      }
    }
  }, [inProgress, oauthReturnHandled]);

  // Wait while MSAL is processing interactions (overlay covers this)
  if (inProgress === "startup" || inProgress === "handleRedirect") {
    return null;
  }

  // Wait while handling OAuth return (overlay covers this)
  if (!oauthReturnHandled) {
    return null;
  }

  // Signed in with a mobile number: no MSAL account, so the MSAL templates
  // don't apply. The pass is checked on every API call like any token.
  const phoneSession = activePhoneSession();
  // Settled during render, before any child's request reads it.
  if (phoneSession) {
    settleTabSignIn(true);
    return <PhoneSignedInApp session={phoneSession} />;
  }
  if (accounts.length > 0) settleTabSignIn(false);

  return (
    <>
      {/* Show ApplicationBlock when user is authenticated */}
      <AuthenticatedTemplate>
        <ApplicationBlock />
      </AuthenticatedTemplate>

      {/* Trigger login and show loader when user is not authenticated */}
      <UnauthenticatedTemplate>
        <LoginHandler />
      </UnauthenticatedTemplate>
    </>
  );
};

/**
 * App initialization wrapper
 * Handles MSAL instance creation and initialization
 */
// Lightweight loading spinner for public pages
const SimpleLoader = () => (
  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
    <div
      style={{
        width: 40,
        height: 40,
        border: "3px solid #f3f3f3",
        borderTop: "3px solid #0078d4",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
      }}
    />
    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
  </div>
);

const AppWithMsal = ({ msalInstance }: { msalInstance: PublicClientApplication }) => {
  const url = window.location.href;

  const signPattern = /^\/sign\/[a-zA-Z0-9_-]{20,}$/;
  const surveyPattern = /^\/survey\/[a-zA-Z0-9_-]+/;
  const isPublicRoute =
    signPattern.test(window.location.pathname) || surveyPattern.test(window.location.pathname);

  // Public routes have their own loaders — dismiss the startup overlay immediately
  useEffect(() => {
    if (isPublicRoute) signalAppReady();
  }, [isPublicRoute]);

  // Public CSAT survey route - no auth needed
  if (surveyPattern.test(window.location.pathname)) {
    return (
      <SkyProvider
        theme={skyTheme}
        defaultMode="light"
        toastConfig={{ position: "top-center", maxToasts: 5, defaultDuration: 5000 }}
      >
        <Provider store={store}>
          <BrowserRouter>
            <Suspense fallback={<SimpleLoader />}>
              <Routes>
                <Route
                  path="/survey/:token"
                  element={<SurveyPage />}
                />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </Provider>
      </SkyProvider>
    );
  }

  // Public packet signing route - minimal setup, no auth needed
  // Match /sign/:accessToken where accessToken is a UUID-like token (20+ chars)
  // Excludes admin routes like /sign/templates, /sign/envelopes, /sign/documents, /sign/contacts
  if (signPattern.test(window.location.pathname)) {
    return (
      <SkyProvider
        theme={skyTheme}
        defaultMode="light"
        toastConfig={{
          position: "top-center",
          maxToasts: 5,
          defaultDuration: 5000,
        }}
      >
        <Provider store={store}>
          <BrowserRouter>
            <Suspense fallback={<SimpleLoader />}>
              <Routes>
                <Route
                  path="/sign/:accessToken"
                  element={<div>Legacy signing route - Please use support tickets</div>}
                />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </Provider>
      </SkyProvider>
    );
  }

  // Protected routes wrapped with MsalProvider
  return (
    <SkyProvider
      theme={skyTheme}
      defaultMode="light"
      toastConfig={{
        position: "top-center",
        maxToasts: 5,
        defaultDuration: 5000,
      }}
    >
      <ThemeSyncer />
      <ConfirmationProvider>
        <Provider store={store}>
          <PersistGate
            loading={null}
            persistor={persistor}
          >
            <MsalProvider instance={msalInstance}>
              <AuthenticatedApp />
            </MsalProvider>
          </PersistGate>
        </Provider>
      </ConfirmationProvider>
    </SkyProvider>
  );
};

/**
 * Root component with async MSAL initialization
 */
const AppRoot = () => {
  const [msalInstance, setMsalInstanceState] = useState<PublicClientApplication | null>(null);
  const [initError, setInitError] = useState<Error | null>(null);
  const [appReady, setAppReady] = useState(false);

  useEffect(() => onAppReady(() => setAppReady(true)), []);

  useEffect(() => {
    // Initialize MSAL instance (v4 requires async initialization)
    createMsalInstance()
      .then((instance) => {
        // Set instance for auth service
        setMsalInstance(instance);
        setMsalInstanceState(instance);
      })
      .catch((error) => {
        skypointLogger.error(
          "Failed to initialize MSAL",
          error instanceof Error ? error : undefined,
        );
        setInitError(error);
      });
  }, []);

  useEffect(() => {
    if (initError) signalAppReady();
  }, [initError]);

  if (initError) {
    return (
      <SkyProvider
        theme={skyTheme}
        defaultMode="light"
        toastConfig={{
          position: "top-center",
          maxToasts: 5,
          defaultDuration: 5000,
        }}
      >
        <MessageBlock>
          <h3>Authentication initialization failed</h3>
          <p>Please refresh the page. If the problem persists, contact support.</p>
        </MessageBlock>
      </SkyProvider>
    );
  }

  return (
    <>
      <SpinnerFix />
      {!appReady && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "#fff" }}>
          <SkyProvider
            theme={skyTheme}
            defaultMode="light"
            toastConfig={{
              position: "top-center",
              maxToasts: 5,
              defaultDuration: 5000,
            }}
          >
            <SkypointLogoLoader />
          </SkyProvider>
        </div>
      )}
      {msalInstance && <AppWithMsal msalInstance={msalInstance} />}
    </>
  );
};

createRoot(document.getElementById("root")!).render(<AppRoot />);
